import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  Divider,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  LinearProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  AttachFile as FileIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import axios from "axios";
import { getMultipartHeader, toastError } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";
import MediaSelector from "../../widgets/MediaSelector";
import MediaRequirementsAccordion from "./WabaMediaRequirements";
import WabaButtonSelector from "./WabaButtonSelector";

// Media upload component
const MediaUploader = ({ onUploadComplete, mediaType, currentMedia }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const acceptedFileTypes = {
    IMAGE: ["image/jpeg", "image/png", "image/webp"],
    VIDEO: ["video/mp4", "video/3gpp"],
    DOCUMENT: [
      "application/pdf",
      "application/vnd.ms-powerpoint",
      "application/msword",
    ],
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!acceptedFileTypes[mediaType].includes(file.type)) {
      setError(
        `Invalid file type. Supported formats: ${acceptedFileTypes[
          mediaType
        ].join(", ")}`
      );
      return;
    }

    // Validate file size (WABA limits: 5MB for images, 16MB for videos/documents)
    const maxSize = mediaType === "IMAGE" ? 5 * 1024 * 1024 : 16 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File too large. Maximum size: ${maxSize / (1024 * 1024)}MB`);
      return;
    }

    setError("");
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${API_ENDPOINT}/upload/media/upload-asset`,
        formData,
        {
          ...getMultipartHeader(),
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percentCompleted);
            }
          },
        }
      );
      const result = response.data;

      onUploadComplete({
        url: result.previewUrl,
        id: result.mediaId,
        mime_type: result.mime_type,
        file_name: result.file_name,
        asset: result.assetHandle,
      });
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } };
      handleFileSelect(fakeEvent);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box borderRadius={2} bgcolor={"#f0f0f0"} height={"100%"} p={2}>
            <MediaSelector
              type={mediaType?.toLowerCase()}
              resetTrue={true}
              onMediaSelector={(newV) => {
                onUploadComplete({
                  url: newV.url,
                  id: newV.mediaId,
                  file_name: newV.fileName,
                  asset: newV.asset,
                });
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept={acceptedFileTypes[mediaType].join(",")}
            style={{ display: "none" }}
          />

          {currentMedia ? (
            <Box sx={{ textAlign: "center", p: 2 }}>
              {mediaType === "IMAGE" ? (
                <img
                  src={currentMedia.url}
                  alt="Uploaded media"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 200,
                    borderRadius: 8,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                />
              ) : mediaType === "VIDEO" ? (
                <video
                  src={currentMedia.url}
                  controls
                  style={{
                    maxWidth: "100%",
                    maxHeight: 200,
                    borderRadius: 8,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                  }}
                >
                  <FileIcon
                    sx={{ fontSize: 48, color: "primary.main", mr: 2 }}
                  />
                  <Box textAlign="left">
                    <Typography variant="body1" fontWeight="bold">
                      {currentMedia.file_name || "Media file"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {mediaType.toLowerCase()} • {currentMedia.mime_type}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Button
                variant="outlined"
                onClick={() => fileInputRef.current?.click()}
                sx={{ mt: 2 }}
              >
                Change Media
              </Button>
            </Box>
          ) : (
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                border: "2px dashed",
                borderColor: uploading
                  ? "primary.main"
                  : error
                  ? "error.main"
                  : "grey.300",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                backgroundColor: uploading
                  ? "primary.50"
                  : error
                  ? "error.50"
                  : "grey.50",
                cursor: uploading ? "default" : "pointer",
                transition: "all 0.3s ease",
              }}
              onClick={() => !uploading && fileInputRef.current?.click()}
            >
              {uploading ? (
                <Box>
                  <CircularProgress size={48} sx={{ mb: 2 }} />
                  <Typography variant="body2" gutterBottom>
                    Uploading... {uploadProgress}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{ mt: 1 }}
                  />
                </Box>
              ) : error ? (
                <Box>
                  <ErrorIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body2" color="error">
                    {error}
                  </Typography>
                  <Button variant="text" sx={{ mt: 1 }}>
                    Try Again
                  </Button>
                </Box>
              ) : (
                <Box>
                  <UploadIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body2" gutterBottom>
                    Click to upload or drag and drop
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {mediaType === "IMAGE" && "JPEG, PNG, WEBP (Max 5MB)"}
                    {mediaType === "VIDEO" && "MP4, 3GPP (Max 16MB)"}
                    {mediaType === "DOCUMENT" && "PDF, PPT, DOC (Max 16MB)"}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {currentMedia && (
            <Box sx={{ mt: 1, p: 1, bgcolor: "success.50", borderRadius: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 16, mr: 1 }} />
                <Typography variant="caption" color="success.main">
                  Media uploaded successfully
                </Typography>
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
// Enhanced Carousel card component
const CarouselCard = ({ card, index, onEdit, onDelete, onReorder }) => {
  const getMediaIcon = () => {
    switch (card.mediaType) {
      case "IMAGE":
        return <ImageIcon color="primary" />;
      case "VIDEO":
        return <VideoIcon color="primary" />;
      case "DOCUMENT":
        return <FileIcon color="primary" />;
      default:
        return <ImageIcon color="disabled" />;
    }
  };

  const renderMediaPreview = () => {
    if (!card.media) return null;

    if (card.mediaType === "IMAGE") {
      return (
        <CardMedia
          component="img"
          height="140"
          image={card.media.url}
          alt={card.header || `Card ${index + 1}`}
          sx={{ bgcolor: "grey.100" }}
        />
      );
    }

    if (card.mediaType === "VIDEO") {
      return (
        <CardMedia
          component="video"
          height="140"
          src={card.media.url}
          controls
          sx={{ bgcolor: "grey.100" }}
        >
          Your browser does not support the video tag.
        </CardMedia>
      );
    }

    return (
      <Box
        sx={{
          height: 140,
          bgcolor: "grey.100",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <FileIcon sx={{ fontSize: 48, color: "grey.400" }} />
        <Box sx={{ ml: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            {card.media.file_name || "Media File"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {card.mediaType.toLowerCase()}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Card sx={{ maxWidth: 300, m: 1, position: "relative" }}>
      {renderMediaPreview()}

      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          {getMediaIcon()}
          <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
            {card.mediaType || "No media"}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {card.body || "No body text"}
        </Typography>

        {card.buttons && card.buttons.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {card.buttons.slice(0, 2).map((button, btnIndex) => (
              <Chip
                key={btnIndex}
                label={button.text}
                size="small"
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ))}
            {card.buttons.length > 2 && (
              <Chip
                label={`+${card.buttons.length - 2}`}
                size="small"
                variant="outlined"
                color="default"
              />
            )}
          </Box>
        )}
      </CardContent>

      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Tooltip title="Edit card">
          <IconButton
            size="small"
            onClick={() => onEdit(index)}
            sx={{ bgcolor: "background.paper", mb: 0.5 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete card">
          <IconButton
            size="small"
            onClick={() => onDelete(index)}
            sx={{ bgcolor: "background.paper" }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ position: "absolute", top: 8, left: 8 }}>
        <Chip
          label={index + 1}
          size="small"
          color="primary"
          sx={{ bgcolor: "primary.main", color: "white" }}
        />
      </Box>
    </Card>
  );
};

// Enhanced Card editor dialog
const CardEditorDialog = ({
  open,
  card,
  index,
  onSave,
  onClose,
  buttonOptions = [],
}) => {
  const [editedCard, setEditedCard] = useState({
    body: "",
    mediaType: "IMAGE",
    media: null,
    buttons: [],
  });

  // Reset form when dialog opens or card prop changes
  useEffect(() => {
    if (open) {
      if (card) {
        // If editing existing card, populate with its data
        setEditedCard(card);
      } else {
        // If adding new card, reset to empty state with buttons based on buttonOptions
        setEditedCard({
          body: "",
          mediaType: "IMAGE",
          media: null,
          buttons: buttonOptions.map((type) => ({ text: "", type })),
        });
      }
    }
  }, [open, card, buttonOptions]);

  const handleMediaUploadComplete = (mediaData) => {
    console.log(mediaData);
    setEditedCard((prev) => ({
      ...prev,
      media: mediaData,
    }));
  };

  const handleButtonChange = (btnIndex, field, value) => {
    setEditedCard((prev) => ({
      ...prev,
      buttons: prev.buttons.map((btn, i) =>
        i === btnIndex ? { ...btn, [field]: value } : btn
      ),
    }));
  };

  const handleMediaTypeChange = (newType) => {
    setEditedCard((prev) => ({
      ...prev,
      mediaType: newType,
      media: null, // Reset media when type changes
    }));
  };

  const prettyType = (type) =>
    type
      .replace(/_/g, " ")
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {index !== null ? `Edit Card ${index + 1}` : "Add New Card"}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl size="small" fullWidth>
              <InputLabel>Media Type</InputLabel>
              <Select
                value={editedCard.mediaType}
                label="Media Type"
                onChange={(e) => handleMediaTypeChange(e.target.value)}
              >
                <MenuItem value="IMAGE">Image</MenuItem>
                <MenuItem value="VIDEO">Video</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <MediaUploader
              mediaType={editedCard.mediaType}
              onUploadComplete={handleMediaUploadComplete}
              currentMedia={editedCard.media}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Body Text"
              value={editedCard.body}
              onChange={(e) =>
                setEditedCard((prev) => ({ ...prev, body: e.target.value }))
              }
              placeholder="Enter body text (max 160 chars)"
              inputProps={{ maxLength: 160 }}
              helperText={`${editedCard.body.length}/160 characters`}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Buttons (Max 2 per card)
            </Typography>

            {editedCard.buttons.map((button, btnIndex) => (
              <Grid
                container
                spacing={2}
                alignItems="center"
                key={btnIndex}
                sx={{ mb: 2 }}
              >
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Button ${btnIndex + 1} Text`}
                    value={button.text}
                    onChange={(e) =>
                      handleButtonChange(btnIndex, "text", e.target.value)
                    }
                    placeholder="Button text (max 20 chars)"
                    inputProps={{ maxLength: 20 }}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Button Type"
                    value={prettyType(button.type)}
                    disabled
                  />
                </Grid>

                {button.type === "URL" && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="URL"
                      value={button.url || ""}
                      onChange={(e) =>
                        handleButtonChange(btnIndex, "url", e.target.value)
                      }
                      placeholder="Enter URL"
                    />
                  </Grid>
                )}

                {button.type === "PHONE_NUMBER" && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone Number"
                      value={button.phone_number || ""}
                      onChange={(e) =>
                        handleButtonChange(
                          btnIndex,
                          "phone_number",
                          e.target.value
                        )
                      }
                      placeholder="Enter phone number"
                    />
                  </Grid>
                )}
              </Grid>
            ))}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => onSave(editedCard)}
          variant="contained"
          disabled={!editedCard.body || !editedCard.media}
        >
          Save Card
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// Enhanced Main WABA Carousel Creator component
const WabaCarouselCreator = ({ onUpdatedCards }) => {
  const [cards, setCards] = useState([]);
  const [buttonOptions, setButtonOptions] = useState([]);
  const [editingCardIndex, setEditingCardIndex] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  useEffect(() => {
    if (onUpdatedCards && typeof onUpdatedCards === "function") {
      onUpdatedCards(cards);
    }
  }, [cards, onUpdatedCards]);
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddCard = () => {
    if (cards.length >= 10) {
      showSnackbar("Maximum 10 cards allowed per carousel", "error");
      return;
    }
    setEditingCardIndex(null);
    setIsEditorOpen(true);
  };

  const handleEditCard = (index) => {
    setEditingCardIndex(index);
    setIsEditorOpen(true);
  };

  const handleDeleteCard = (index) => {
    setCards((prev) => prev.filter((_, i) => i !== index));
    showSnackbar("Card deleted successfully");
  };

  const handleSaveCard = (cardData) => {
    if (editingCardIndex !== null) {
      // Edit existing card
      setCards((prev) =>
        prev.map((card, i) => (i === editingCardIndex ? cardData : card))
      );
      showSnackbar("Card updated successfully");
    } else {
      // Add new card
      setCards((prev) => [...prev, cardData]);
      showSnackbar("Card added successfully");
    }
    setIsEditorOpen(false);
    setEditingCardIndex(null);
  };

  const generateTemplateJSON = () => {
    const template = {
      name: `carousel_template_${Date.now()}`,
      category: "MARKETING",
      components: [
        {
          type: "HEADER",
          format: "CAROUSEL",
          cards: cards.map((card) => ({
            header: { text: card.header },
            body: { text: card.body },
            media: {
              type: card.mediaType,
              url: card.media.url,
              id: card.media.id,
            },
            buttons: card.buttons.map((btn) => ({
              type: btn.type,
              text: btn.text,
            })),
          })),
        },
      ],
    };
    return JSON.stringify(template, null, 2);
  };

  const handleExport = () => {
    const json = generateTemplateJSON();
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "waba_carousel_template.json";
    a.click();
    URL.revokeObjectURL(url);
    showSnackbar("Template exported successfully");
  };

  return (
    <Paper elevation={0} sx={{ mx: "auto", my: 2 }}>
      {/* Media Requirements */}
      <MediaRequirementsAccordion />
      <WabaButtonSelector
        onSelectionChange={(newV) => {
          setButtonOptions(newV);
        }}
      />

      {/* Action buttons */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCard}
          disabled={cards.length >= 10}
        >
          Add Card ({cards.length}/10)
        </Button>

        <Button
          variant="outlined"
          onClick={handleExport}
          disabled={cards.length === 0}
        >
          Export Template
        </Button>

        <Box sx={{ flexGrow: 1 }} />

        <Chip
          label={`${cards.length} card${cards.length !== 1 ? "s" : ""} created`}
          color={cards.length > 0 ? "success" : "default"}
          variant="outlined"
        />
      </Box>
      {/* Cards grid */}
      {cards.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            textAlign: "center",
            p: 6,
            bgcolor: "grey.50",
            borderStyle: "dashed",
          }}
        >
          <UploadIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No cards created yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Start by adding your first carousel card with media upload
          </Typography>
          <Button variant="contained" onClick={handleAddCard}>
            Create First Card
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {cards.map((card, index) => (
            <CarouselCard
              key={index}
              card={card}
              index={index}
              onEdit={handleEditCard}
              onDelete={handleDeleteCard}
            />
          ))}
        </Box>
      )}

      {/* Validation alerts */}
      {cards.length > 0 && (
        <Box sx={{ mt: 3 }}>
          {cards.length < 2 && (
            <Alert severity="warning">
              Carousel templates work best with at least 2 cards. Add more cards
              for better engagement.
            </Alert>
          )}

          {cards.some((card) => !card.media) && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Some cards are missing media. Media upload is required for
              carousel templates.
            </Alert>
          )}
        </Box>
      )}

      {/* Card editor dialog */}
      <CardEditorDialog
        buttonOptions={buttonOptions}
        open={isEditorOpen}
        card={editingCardIndex !== null ? cards[editingCardIndex] : null}
        index={editingCardIndex}
        onSave={handleSaveCard}
        onClose={() => {
          setIsEditorOpen(false);
          setEditingCardIndex(null);
        }}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default WabaCarouselCreator;
